/* eslint-disable sonarjs/cognitive-complexity */
/* istanbul ignore file
 */
import {
  getIsUserPersonalizationEnabled,
  getUserPersonalizationRefreshTime,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { isClient } from '@tcp/core/src/utils';
import { getLocalStorage, setLocalStorage } from './localStorageManagement';
import { getDateObject, getIsGuestUser } from './utils';
import { getStoreRef } from './store.utils';

const USER_PERSONALIZATION_KEY = 'userPersonalization';

const formatCategoryKey = (categoryKey) => {
  const tempKey = categoryKey.split('|')[0];
  if (tempKey) {
    const catKeyList = tempKey.split('>');
    return catKeyList[catKeyList.length - 1];
  }
  return categoryKey.split('|')[0];
};

/**
 * This function will return the previously selected preference for the given category
 * style name If moduleClassName has spacial string to update the class name.
 * @param {Array} categoryMap Category list for the selected Product
 */
export const getUserData = (storeSelection, categoryMap) => {
  let userSelection;
  const { data } = storeSelection;
  if (data && storeSelection?.expiryTime > getDateObject().getTime()) {
    categoryMap?.forEach((category) => {
      // split needed to get only category Ids from 47511>49005>742009|Girl>Dresses, Rompers & Shortalls>Casual Dresses
      const categoryKey = formatCategoryKey(category);
      const index = data?.findIndex((item) => item.categoryId === categoryKey);
      if (index > -1) {
        userSelection = data[index];
      }
    });
  }
  return userSelection;
};
/**
 * This function will return the previously selected preference for the given category
 * style name If moduleClassName has spacial string to update the class name.
 * @param {Array} categoryMap Category list for the selected Product
 */
export const getUserSelection = (categoryMap) => {
  let userSelection;
  if (getIsUserPersonalizationEnabled(getStoreRef().getState()) && isClient()) {
    const storeSelection = JSON.parse(getLocalStorage(USER_PERSONALIZATION_KEY) || '{}');
    if (storeSelection?.expiryTime < getDateObject().getTime()) {
      // Logged in user has expiry time. Need to fetch data from API if its expired
      window.localStorage.removeItem(USER_PERSONALIZATION_KEY);
      storeSelection.data = null;
    }
    userSelection = getUserData(storeSelection, categoryMap);
  }
  return userSelection;
};

/**
 * This function will update the user selection from PDP/QV pages. Ex : size/Fit selection
 * style name If moduleClassName has spacial string to update the class name.
 * @param {Array} categoryMap Category list for the selected Product Ex: [
      "47511>49005>742009|Girl>Dresses, Rompers & Shortalls>Casual Dresses",
      "47511>49005>54160|Girl>Dresses, Rompers & Shortalls>Dresses"
   ]
 * @param {Map} selectionMap Category list for the selected Product Ex : {'size' : 'xs', 'fit' : 'Regular'}
 */
export const setUserSelection = (categoryMap, selectionMap, saveRecentSelection) => {
  if (
    getIsUserPersonalizationEnabled(getStoreRef().getState()) &&
    selectionMap &&
    Object.keys(selectionMap).length > 0 &&
    categoryMap &&
    categoryMap.length > 0
  ) {
    const INITIAL_STATE = {
      data: [],
      expiryTime: null,
      recentSelection: [],
    };
    // For Guest/Logged in User save item in local host
    let storeItem = '';
    storeItem = getLocalStorage(USER_PERSONALIZATION_KEY);
    let storeSelection = storeItem ? JSON.parse(storeItem || '{}') : INITIAL_STATE;
    const storeData = storeSelection ? [...storeSelection.data] : [];
    const recentSelection = [];

    categoryMap.forEach((category) => {
      // split needed to get only category Ids from 47511>49005>742009|Girl>Dresses, Rompers & Shortalls>Casual Dresses
      const categoryKey = formatCategoryKey(category);
      const index = storeData.findIndex((item) => item.categoryId === categoryKey);
      if (index > -1) {
        storeData[index] = { ...storeData[index], ...selectionMap };
      } else {
        storeData.push({ categoryId: categoryKey, ...selectionMap });
      }
      if (saveRecentSelection) {
        recentSelection.push({ categoryId: categoryKey, ...selectionMap });
      }
    });
    // updating the StoreSelection
    storeSelection = {
      data: [...storeData],
      expiryTime:
        getDateObject().getTime() + getUserPersonalizationRefreshTime(getStoreRef()?.getState()),
      recentSelection,
    };
    setLocalStorage({ key: USER_PERSONALIZATION_KEY, value: JSON.stringify(storeSelection) });
  }
};

/**
 * This function will return rencent selection made by user
 */
export const getRecentSelection = () => {
  let recentSelection;
  if (getIsUserPersonalizationEnabled(getStoreRef().getState())) {
    const storeItem = getLocalStorage(USER_PERSONALIZATION_KEY);
    const storeSelection = JSON.parse(storeItem || '[]');
    recentSelection = storeSelection?.recentSelection;
  }
  return recentSelection;
};

export const hasValidUserSelection = () => {
  if (getIsUserPersonalizationEnabled(getStoreRef().getState()) && isClient()) {
    const storeSelection = JSON.parse(getLocalStorage(USER_PERSONALIZATION_KEY) || '{}');
    const storeData = storeSelection?.data;

    if (
      !getIsGuestUser() &&
      (!storeData || storeSelection?.expiryTime <= getDateObject().getTime())
    ) {
      // Logged in user has expiry time. Need to fetch data from API if its expired
      window.localStorage.removeItem(USER_PERSONALIZATION_KEY);
      return false;
    }
  }
  return true;
};

export const updateStoreWithAPIResponse = (browseData) => {
  const refreshTime = getUserPersonalizationRefreshTime(getStoreRef()?.getState());
  const dataRes = browseData || [];
  const itemToSet = dataRes.map((i) => {
    const item = { ...i };
    // eslint-disable-next-line no-underscore-dangle
    delete item.__typename;
    return item;
  });

  let storeItem = '';
  storeItem = getLocalStorage(USER_PERSONALIZATION_KEY);
  let storeSelection = storeItem ? JSON.parse(storeItem || '{}') : {};
  let storeData = storeSelection && storeSelection.data ? storeSelection.data : [];

  if (storeData && storeData.length > 0) {
    itemToSet.forEach((item) => {
      // split needed to get only category Ids from 47511>49005>742009|Girl>Dresses, Rompers & Shortalls>Casual Dresses
      const categoryKey = item.categoryId;
      const index = storeData.findIndex((mItem) => mItem.categoryId === categoryKey);
      if (index > -1) {
        storeData[index] = { ...storeData[index], ...{ fit: item.fit, size: item.size } };
      } else {
        storeData.push(item);
      }
    });
  } else {
    storeData = itemToSet;
  }

  storeSelection = {
    data: [...storeData],
    expiryTime: getDateObject().getTime() + refreshTime,
  };
  setLocalStorage({ key: USER_PERSONALIZATION_KEY, value: JSON.stringify(storeSelection) });
};

export default {
  getUserSelection,
  setUserSelection,
  getRecentSelection,
  hasValidUserSelection,
  updateStoreWithAPIResponse,
};
